import { useState, createElement } from "react";
import { Plus, Trash2, Save, GitBranch } from "lucide-react";
import { Button } from "@/components/ui/button";

const uid = () => Math.random().toString(36).slice(2, 9);
const COLORS = ["bg-slate-900 text-white", "bg-blue-700 text-white", "bg-cyan-600 text-white", "bg-emerald-600 text-white", "bg-amber-500 text-white", "bg-rose-600 text-white"];

export function defaultMindMap(title) {
  return { id: "root", text: title || "Masalah utama", children: [{ id: uid(), text: "Why 1 - Mengapa terjadi?", children: [] }, { id: uid(), text: "Faktor Man / Machine / Method / Material", children: [] }] };
}

function fiveWhy(text) {
  let acc = [];
  for (let i = 5; i >= 1; i--) acc = [{ id: uid(), text: `Why ${i} - `, children: acc }];
  return { id: "root", text, children: acc };
}

function fishbone(text) {
  const cats = ["Man", "Machine", "Method", "Material", "Measurement", "Environment"];
  return { id: "root", text, children: cats.map((t) => ({ id: uid(), text: t, children: [] })) };
}

function countNodes(n) {
  return 1 + n.children.reduce((a, c) => a + countNodes(c), 0);
}

function MindNode({ node, depth, onChange, onDelete, readOnly }) {
  const update = (patch) => onChange({ ...node, ...patch });
  const setChild = (i, c) => update({ children: node.children.map((x, j) => (j === i ? c : x)) });
  const delChild = (i) => update({ children: node.children.filter((_, j) => j !== i) });
  const addChild = () => update({ children: [...node.children, { id: uid(), text: "", children: [] }] });
  const rows = Math.max(1, Math.ceil((node.text.length + 1) / 26));
  return (
    <div className="flex items-start">
      <div className={`group relative rounded-md px-2 py-1 shadow-sm min-w-[160px] max-w-[220px] ${COLORS[depth % COLORS.length]}`} data-testid={`mind-node-${node.id}`}>
        <textarea data-testid={`mind-node-text-${node.id}`} readOnly={readOnly} value={node.text} onChange={(e) => update({ text: e.target.value })} rows={rows} placeholder="Tulis ide / penyebab / tindakan" className="w-full bg-transparent text-xs resize-none outline-none placeholder:opacity-60 leading-snug" />
        {!readOnly && (
          <div className="absolute -right-2 -top-2 flex gap-0.5 opacity-70 group-hover:opacity-100">
            <button type="button" data-testid={`mind-node-add-${node.id}`} onClick={addChild} title="Tambah cabang" className="w-5 h-5 rounded-full bg-white text-slate-900 border shadow flex items-center justify-center hover:bg-slate-100"><Plus size={11} /></button>
            {depth > 0 && <button type="button" data-testid={`mind-node-del-${node.id}`} onClick={onDelete} title="Hapus" className="w-5 h-5 rounded-full bg-white text-rose-600 border shadow flex items-center justify-center hover:bg-rose-50"><Trash2 size={11} /></button>}
          </div>
        )}
      </div>
      {node.children.length > 0 && (
        <div className="flex items-start">
          <div className="w-6 h-px bg-slate-300 mt-4" />
          <div className="flex flex-col gap-2 border-l border-slate-300 pl-4">
            {node.children.map((c, i) => (
              <div key={c.id} className="relative">
                <div className="absolute -left-4 top-4 w-4 h-px bg-slate-300" />
                {createElement(MindNode, { node: c, depth: depth + 1, onChange: (nc) => setChild(i, nc), onDelete: () => delChild(i), readOnly })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function MindMap({ value, onSave, readOnly, title }) {
  const [tree, setTree] = useState(value || defaultMindMap(title));
  const [dirty, setDirty] = useState(false);
  const apply = (t) => { setTree(t); setDirty(true); };
  return (
    <div className="card-flat p-4" data-testid="mind-map">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="label-caps flex items-center gap-1"><GitBranch size={12} /> Mind Map / Pohon Berpikir</div>
          <div className="text-[11px] text-slate-500">Arahkan kursor ke kotak lalu klik + untuk menambah cabang atau ikon tempat sampah untuk menghapus. Cocok untuk 5 Why, fishbone, atau pohon solusi.</div>
        </div>
        {!readOnly && (
          <div className="flex gap-2 flex-wrap">
            <Button type="button" size="sm" variant="outline" data-testid="mind-map-template-5why" onClick={() => apply(fiveWhy(tree.text))}>Template 5 Why</Button>
            <Button type="button" size="sm" variant="outline" data-testid="mind-map-template-fishbone" onClick={() => apply(fishbone(tree.text))}>Template Fishbone</Button>
            <Button type="button" size="sm" data-testid="mind-map-save" disabled={!dirty} onClick={async () => { await onSave(tree); setDirty(false); }}><Save size={14} className="mr-1" />Simpan Mind Map</Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto pb-2">
        <MindNode node={tree} depth={0} readOnly={readOnly} onChange={apply} onDelete={() => {}} />
      </div>
      <div className="text-[10px] text-slate-400 mt-2" data-testid="mind-map-count">{countNodes(tree)} node{dirty ? " - belum disimpan" : ""}</div>
    </div>
  );
}
